// Top tab strip — browser-like tabs for viewing multiple screens at once.
// Shown in the installed/standalone program mode (see AppShell).
//  · click = switch · ＋ = new tab (dashboard) · ×/middle-click/Ctrl+W = close
//  · Ctrl+Q = quit program · drag = reorder
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { I } from "../shared/Icon";
import { moduleById } from "./modules";
import { useTabs } from "./tabsStore";

export function TabBar() {
  const nav = useNavigate();
  const tabs = useTabs((s) => s.tabs);
  const activeId = useTabs((s) => s.activeId);
  const setActive = useTabs((s) => s.setActive);
  const openNew = useTabs((s) => s.openNew);
  const close = useTabs((s) => s.close);
  const reorder = useTabs((s) => s.reorder);

  const dragId = useRef<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const switchTo = (id: string, url: string) => { setActive(id); nav(url); };

  const closeTab = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    e.preventDefault();
    const url = close(id);
    if (url) nav(url);
  };

  // "+" → 새 탭, 기본 페이지는 대시보드
  const addTab = () => {
    const m = moduleById("dashboard");
    const url = m?.path ?? "/dashboard";
    openNew({ url, title: m?.label ?? "대시보드", icon: (m?.icon ?? "Home") as any, color: m?.color ?? "var(--p-500)" });
    nav(url);
  };

  // Ctrl/Cmd+W = 활성 탭만 닫기 · Ctrl/Cmd+Q = 프로그램 종료
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && k === "w") {
        e.preventDefault();
        const st = useTabs.getState();
        if (st.tabs.length > 1 && st.activeId) {
          const url = st.close(st.activeId);
          if (url) nav(url);
        }
      } else if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && k === "q") {
        e.preventDefault();
        window.close(); // 설치형(standalone) 프로그램 창 종료
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nav]);

  return (
    <div className="t-tabbar">
      <div className="t-tabbar-scroll">
        {tabs.map((t) => {
          const Ico = (I as any)[t.icon];
          const active = t.id === activeId;
          return (
            <div
              key={t.id}
              className={`t-tab ${active ? "active" : ""} ${overId === t.id ? "dragover" : ""}`}
              draggable
              onDragStart={(e) => { dragId.current = t.id; e.dataTransfer.effectAllowed = "move"; }}
              onDragOver={(e) => { e.preventDefault(); if (dragId.current && dragId.current !== t.id) setOverId(t.id); }}
              onDragLeave={() => setOverId((o) => (o === t.id ? null : o))}
              onDrop={(e) => { e.preventDefault(); if (dragId.current && dragId.current !== t.id) reorder(dragId.current, t.id); dragId.current = null; setOverId(null); }}
              onDragEnd={() => { dragId.current = null; setOverId(null); }}
              onClick={() => switchTo(t.id, t.url)}
              onAuxClick={(e) => { if (e.button === 1 && tabs.length > 1) closeTab(e, t.id); }}
              title={t.title}
            >
              <span className="t-tab-dot" style={{ background: t.color }} />
              {Ico ? <Ico size={13} /> : null}
              <span className="t-tab-label">{t.title}</span>
              {tabs.length > 1 && (
                <button className="t-tab-x" onClick={(e) => closeTab(e, t.id)} title="닫기 (Ctrl+W · 가운데 클릭)">×</button>
              )}
            </div>
          );
        })}
      </div>
      <button className="t-tab-new" onClick={addTab} title="새 탭 (대시보드)">＋</button>
    </div>
  );
}
